import { supabase } from "@/lib/supabase";
import type { Account, Profile } from "@/lib/types";

/**
 * select("*") deliberately, rather than an explicit column list: this way,
 * if migration 0003 (full_name/phone_number/phone_type) hasn't been applied
 * to a given environment yet, the query still succeeds — the new fields are
 * just absent from the returned row — instead of PostgREST 400ing the whole
 * query and silently breaking full_name/language everywhere getProfile()
 * is used (i18n, identity, AccountPage).
 */
export async function getProfile(): Promise<Profile | null> {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) return null;
  const { data, error } = await supabase.from("profiles").select("*").eq("id", u.user.id).maybeSingle();
  if (error) throw error;
  return (data as Profile | null) ?? null;
}

/** Update the signed-in user's own personal profile fields (name, phone, language). */
export async function updateProfile(fields: Partial<Omit<Profile, "id">>): Promise<void> {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) throw new Error("Not signed in");
  const { error } = await supabase.from("profiles").update(fields).eq("id", u.user.id);
  if (error) throw error;
}

/**
 * The signed-in user's account (business identity) — see supabase/migrations
 * /0023 (backlog AUTH-3). RLS (`is_account_member()`) already scopes this to
 * whichever account the caller belongs to, so no explicit filter is needed
 * beyond `.maybeSingle()` — same pattern `getSubscription()` (src/lib/billing.ts)
 * already uses for an implicitly-RLS-scoped single row. Returns null for a
 * signed-out caller or (defensively; shouldn't happen post-AUTH-3) a user
 * with no account membership yet.
 */
export async function getAccount(): Promise<Account | null> {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) return null;
  const { data, error } = await supabase.from("accounts").select("*").maybeSingle();
  if (error) throw error;
  return (data as Account | null) ?? null;
}

/**
 * The signed-in user's account_id — for stamping onto new rows in the 5
 * account_id-scoped content tables (documents/clients/contracts/
 * catalog_items/templates, migration 0024) at insert time. Throws rather
 * than returning null: every real user has exactly one membership row
 * post-AUTH-3's backfill, so a missing one means something is actually
 * wrong, not a normal empty state a caller should silently handle.
 */
export async function requireAccountId(): Promise<string> {
  const { data: u, error: userError } = await supabase.auth.getUser();
  if (userError || !u.user) throw new Error("Not signed in");
  const { data: membership, error } = await supabase
    .from("account_members")
    .select("account_id")
    .eq("user_id", u.user.id)
    .maybeSingle();
  if (error) throw error;
  if (!membership) throw new Error("No account found for the signed-in user");
  return membership.account_id as string;
}

/**
 * Update the signed-in user's account (business identity) — every member
 * can edit it today (no Owner-only gating yet; that's backlog AUTH-4).
 * Explicitly resolves and filters by the caller's own account_id, rather
 * than relying on RLS alone to scope an unfiltered `.update()` — one user
 * maps to exactly one account today, but AUTH-5 makes multi-account
 * membership possible, and an unfiltered update would then silently touch
 * every account the caller belongs to instead of just one.
 */
export async function updateAccount(fields: Partial<Omit<Account, "id">>): Promise<void> {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) throw new Error("Not signed in");
  const { data: membership, error: membershipError } = await supabase
    .from("account_members")
    .select("account_id")
    .eq("user_id", u.user.id)
    .maybeSingle();
  if (membershipError) throw membershipError;
  if (!membership) throw new Error("No account found for the signed-in user");
  const { error } = await supabase.from("accounts").update(fields).eq("id", membership.account_id);
  if (error) throw error;
}

/** "1420 Grant Ave, Coppell, TX 75019" — skips any part that's still unset. */
export function businessAddress(account: Pick<Account, "business_street" | "business_city" | "business_state" | "business_zip">): string {
  const cityStateZip = [account.business_city, [account.business_state, account.business_zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  return [account.business_street, cityStateZip].filter(Boolean).join(", ");
}
